import { type RefObject, useLayoutEffect, useState } from 'react';

/** Distância mínima entre o painel e a borda da janela. */
const MARGEM_DA_JANELA = 8;

/** Abaixo disso o painel não encolhe — vira de lado ou cobre o botão. */
const ALTURA_MINIMA = 200;

export interface PosicaoFlutuante {
  top: number;
  left: number;
  /** Altura que cabe — igual à pedida quando sobra espaço, menor em tela baixa. */
  altura: number;
}

interface OpcoesDaPosicao {
  largura: number;
  altura: number;
  lado: 'top' | 'bottom';
  folga?: number;
}

/**
 * O cálculo em si, sem DOM (testável): retângulo do botão + tamanho da janela.
 * Regras no comentário de `usePosicaoFlutuante`.
 */
export function calcularPosicao(
  ancora: { top: number; bottom: number; left: number },
  janela: { largura: number; altura: number },
  { largura, altura, lado, folga = 8 }: OpcoesDaPosicao,
): PosicaoFlutuante {
  const espacoAbaixo = janela.altura - MARGEM_DA_JANELA - (ancora.bottom + folga);
  const espacoAcima = ancora.top - folga - MARGEM_DA_JANELA;
  const outroLado = lado === 'bottom' ? 'top' : 'bottom';
  const espaco = (l: 'top' | 'bottom'): number => (l === 'bottom' ? espacoAbaixo : espacoAcima);

  const ladoFinal =
    espaco(lado) >= altura
      ? lado
      : espaco(outroLado) >= altura
        ? outroLado
        : espacoAbaixo >= espacoAcima
          ? 'bottom'
          : 'top';
  const alturaFinal = Math.max(Math.min(altura, espaco(ladoFinal)), ALTURA_MINIMA);
  const top =
    ladoFinal === 'bottom'
      ? Math.min(ancora.bottom + folga, janela.altura - MARGEM_DA_JANELA - alturaFinal)
      : Math.max(ancora.top - folga - alturaFinal, MARGEM_DA_JANELA);
  const left = Math.min(
    Math.max(ancora.left, MARGEM_DA_JANELA),
    janela.largura - largura - MARGEM_DA_JANELA,
  );
  return {
    top: Math.max(top, MARGEM_DA_JANELA),
    left: Math.max(left, MARGEM_DA_JANELA),
    altura: alturaFinal,
  };
}

/**
 * Posição (`position: fixed`) de um painel que abre preso a um botão mas
 * DESENHADO NO BODY (portal) — pra escapar de pai com rolagem própria, como o
 * corpo de um modal, que cortava o painel na área visível dele.
 *
 * Abre do `lado` pedido se couber inteiro; senão do outro lado, se lá couber.
 * Tela baixa demais pros dois: vai pro lado com mais espaço e ENCOLHE até
 * caber (o painel rola por dentro) — antes saía cortado no pé da tela. Nunca
 * sai da janela pelos lados. Recalcula ao rolar (qualquer rolagem: o botão
 * pode estar dentro de um modal que rola) e ao redimensionar. Fechado = `null`.
 */
export function usePosicaoFlutuante(
  ancoraRef: RefObject<HTMLElement | null>,
  aberto: boolean,
  opcoes: OpcoesDaPosicao,
): PosicaoFlutuante | null {
  const { largura, altura, lado, folga = 8 } = opcoes;
  const [posicao, setPosicao] = useState<PosicaoFlutuante | null>(null);

  useLayoutEffect(() => {
    if (!aberto) {
      setPosicao(null);
      return;
    }
    const calcular = (): void => {
      const el = ancoraRef.current;
      if (!el) return;
      setPosicao(
        calcularPosicao(
          el.getBoundingClientRect(),
          { largura: window.innerWidth, altura: window.innerHeight },
          { largura, altura, lado, folga },
        ),
      );
    };
    calcular();
    window.addEventListener('resize', calcular);
    window.addEventListener('scroll', calcular, true);
    return () => {
      window.removeEventListener('resize', calcular);
      window.removeEventListener('scroll', calcular, true);
    };
  }, [aberto, ancoraRef, largura, altura, lado, folga]);

  return posicao;
}
